import React, {useEffect, useMemo} from "react";
import {useNavigate} from "react-router-dom";

import {useDebugMode} from "../hooks/useDebugMode";
import StoryRail from "./StoryRail";

/**
 * A personalised rail bound to one NSL context.
 *
 * Every showcase surface on the front page is the same component pointed at a
 * different context, which is the whole argument of the demo: the layout is
 * held constant so any difference a visitor sees between rails came from the
 * ranking configuration, not from the page.
 */
const PersonalisedRail = ({
  contextId,
  kicker,
  title,
  standfirst,
  tone,
  debugNote,
  emptyTitle,
  emptyBody,
  fetchRecommendationsForContext,
  data = [],
  isLoading = false,
  viewInterceptor,
}) => {
  const navigate = useNavigate();
  const debugMode = useDebugMode();

  useEffect(() => {
    fetchRecommendationsForContext(contextId ?? "");
  }, [contextId, fetchRecommendationsForContext]);

  const items = useMemo(
    () =>
      (data || [])
        .map((entry) => {
          const metadata = entry?.metadata;
          if (!metadata) return null;
          return {
            id: entry.id,
            title: metadata.originalTitle || entry.name || "Untitled",
            summary: metadata.summary || entry.description || "",
            image: metadata.image || "",
            category: metadata.aiCategorization || metadata.topic || "",
            meta: [metadata.source, metadata.timeToRead]
              .filter(Boolean)
              .join(" · "),
            // base_score is similarity to the reader's profile. The final
            // `score` is that value after damping (impression fatigue can cut
            // it ~15x), which renders as a nonsensical "2% match".
            matchPercentage:
              typeof entry.baseScore === "number"
                ? Math.round(entry.baseScore * 100)
                : null,
            rank: entry.rank ?? null,
            explanation: entry.recommendationExplanation || null,
            source: entry.recommendationSource || "",
            story: {
              uuid: entry.id,
              originalTitle: metadata.originalTitle || entry.name,
              aiSummary: metadata.summary || entry.description,
              aiHtmlDescription: metadata.aiHtmlDescription,
              aiCategorization: metadata.aiCategorization || metadata.topic,
              image: metadata.image,
              source: metadata.source,
              link: metadata.link,
              timeToRead: metadata.timeToRead,
              createdDate: metadata.createdDate,
            },
          };
        })
        .filter(Boolean),
    [data]
  );

  const openStory = (item) => {
    if (!item?.id) return;
    try {
      sessionStorage.setItem(
        `neuron:article:${item.id}`,
        JSON.stringify(item.story)
      );
    } catch {
      // Session storage is best-effort; the article page refetches if absent.
    }
    navigate(`/article/${item.id}`);
  };

  return (
    <StoryRail
      kicker={kicker}
      title={title}
      standfirst={standfirst}
      tone={tone}
      debugNote={debugNote}
      debugMode={debugMode}
      items={items}
      isLoading={isLoading}
      onSelect={openStory}
      getItemRef={(id) => viewInterceptor?.getItemRef(id)}
      emptyTitle={emptyTitle}
      emptyBody={emptyBody}
    />
  );
};

export default PersonalisedRail;
